import map from "lodash/map";
import { useTranslation } from "react-i18next";
import { listSavedWorkoutsForView } from "../../../../utils/saved-workout-list";
import CycleCardCP from "./CycleCardCP";
import SavedWorkoutSearch from "./SavedWorkoutSearch";
import WorkoutFolderDetail from "./WorkoutFolderDetail";
import WorkoutFoldersSection from "./WorkoutFoldersSection";

const sortByName = (a, b) =>
  (a.name || "").localeCompare(b.name || "", undefined, {
    sensitivity: "base",
  });
const sortByNameDesc = (a, b) =>
  (b.name || "").localeCompare(a.name || "", undefined, {
    sensitivity: "base",
  });
const sortByCreatedDate = (a, b) => {
  const dateA = new Date(a.createdDate || 0).getTime();
  const dateB = new Date(b.createdDate || 0).getTime();
  return dateB - dateA;
};

const SORT_FUNCTIONS = {
  name: sortByName,
  nameDescending: sortByNameDesc,
  createdDate: sortByCreatedDate,
};

const CREATE_SAVED_WORKOUT_URL =
  "/actions/refresh/?modal[type]=build-step-one&cycleType=new&isTemplate=true&storeType=single-workout&fromTemplate=true&tabView=single-workouts-tab&templateView=single-workouts";

const SingleWorkoutsTemplates = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const {
    savedWorkouts = [],
    workoutFolders = [],
    query = {},
    activeUserToRequest,
  } = locals;
  const sort = query.sort || "createdDate";
  const sortFn = SORT_FUNCTIONS[sort] || sortByCreatedDate;
  const folderId = query.folderId;
  const search = query.workoutSearch || "";
  const searching = String(search).trim().length > 0;

  const searchBar = (
    <SavedWorkoutSearch workoutSearch={search} folderId={folderId || ""} />
  );

  if (folderId) {
    const folder = workoutFolders.find(
      (f) => String(f._id) === String(folderId)
    );
    if (folder) {
      const workouts = listSavedWorkoutsForView({
        savedWorkouts,
        workoutFolders,
        search,
        folder,
      });
      return (
        <>
          {searchBar}
          <WorkoutFolderDetail
            folder={folder}
            workouts={workouts}
            locals={locals}
            query={query}
            searching={searching}
          />
        </>
      );
    }
  }

  const visibleWorkouts = listSavedWorkoutsForView({
    savedWorkouts,
    workoutFolders,
    search,
  });
  const sortedWorkouts = [...visibleWorkouts].sort(sortFn);
  const hasFolders = workoutFolders.length > 0;
  const showHeader = sortedWorkouts.length > 0;

  return (
    // The folders block and the workout grid are siblings rather than nested,
    // because every card style in Templates.css is scoped to
    // `main > section > .columns > article`.
    <>
      {searchBar}
      {searching && (
        <SavedWorkoutResults
          workouts={sortedWorkouts}
          activeUserId={activeUserToRequest._id}
          t={t}
        />
      )}
      <WorkoutFoldersSection
        folders={workoutFolders}
        savedWorkouts={savedWorkouts}
      />

      {!searching && (
        <section className="single-workouts-uncategorized">
          {showHeader && (
            <hgroup className="single-workouts-uncategorized-header">
              <h3>
                {hasFolders
                  ? t("allTemplates.workoutFolders.allWorkouts", "All Workouts")
                  : t("allTemplates.workoutFolders.workouts", "Workouts")}
              </h3>
              <span className="single-workouts-uncategorized-label">
                {t(
                  "allTemplates.workoutFolders.uncategorized",
                  "(Uncategorized)"
                )}
              </span>
            </hgroup>
          )}

          <div className="columns" id="drag-items">
            {sortedWorkouts.length > 0 ? (
              map(sortedWorkouts, (cycle, cardIndex) => (
                <CycleCardCP
                  key={cycle._id}
                  cycle={cycle}
                  cardIndex={cardIndex}
                  isOwnCycle={cycle.owner === activeUserToRequest._id}
                />
              ))
            ) : (
              <SingleWorkoutsEmptyState
                hasFolders={workoutFolders.length > 0}
              />
            )}
          </div>
        </section>
      )}
    </>
  );
};

const SavedWorkoutResults = ({ workouts, activeUserId, t }) => (
  <section className="single-workouts-uncategorized">
    {workouts.length > 0 && (
      <hgroup className="single-workouts-uncategorized-header">
        <h3>{t("allTemplates.singleWorkouts.searchResults")}</h3>
      </hgroup>
    )}
    <div className="columns" id="drag-items">
      {workouts.length > 0 ? (
        map(workouts, (cycle, cardIndex) => (
          <CycleCardCP
            key={cycle._id}
            cycle={cycle}
            cardIndex={cardIndex}
            isOwnCycle={cycle.owner === activeUserId}
          />
        ))
      ) : (
        <p className="workout-folder-detail-empty">
          {t(
            "allTemplates.workoutFolders.noWorkoutsFound",
            "No workouts found"
          )}
        </p>
      )}
    </div>
  </section>
);

const SingleWorkoutsEmptyState = ({ hasFolders = false }) => {
  const { t } = useTranslation("coaching-portal");

  return (
    <div className="no-teams-section min-h-0!">
      <div className="no-teams-section-card">
        {!hasFolders && (
          <h3>{t("allTemplates.singleWorkouts.noSingleWorkoutsTitle")}</h3>
        )}

        {!hasFolders && (
          <h4 className="pb-3 pt-6">
            {t("allTemplates.singleWorkouts.tagline")}
          </h4>
        )}

        <p
          className={
            hasFolders ? "p2 pb-8 pt-6 text-center" : "p2 pb-8 text-center"
          }
        >
          {hasFolders
            ? t(
                "allTemplates.workoutFolders.allCaughtUpDescription",
                "Every saved workout is organized into a folder. Create a new one or check your folders above."
              )
            : t("allTemplates.singleWorkouts.emptyDescription")}
        </p>
        <a
          role="button"
          className="py-2 px-4"
          data-button-type="primary"
          href={CREATE_SAVED_WORKOUT_URL}
        >
          <img
            alt={t("allTemplates.header.addIconAlt")}
            src="/img/new-icons/add.svg"
          />
          {hasFolders
            ? t(
                "allTemplates.workoutFolders.createNewWorkout",
                "Create New Workout"
              )
            : t("allTemplates.singleWorkouts.createFirstSingleWorkout")}
        </a>
      </div>
    </div>
  );
};

export default SingleWorkoutsTemplates;
